const emit = (target, type, detail) => target.dispatchEvent(new CustomEvent(type, { detail }));

// https://developer.spotify.com/documentation/embeds/references/iframe-api#methods
export default class SpotifyPlayer extends EventTarget {
	static #getApiPromise = null;
	#getPlayerPromise = null;
	#endSeconds = 0;

	constructor(elemId) {
		super();
		this.elemId = elemId;
	}

	static getName() { return 'Spotify'; }
	static urlMatches(url) {
		return new URL(url).host.endsWith('spotify.com');
	}

	static getApi() {
		return SpotifyPlayer.#getApiPromise ||= new Promise((resolve, reject) => {
			window.onSpotifyIframeApiReady = IFrameAPI => resolve(IFrameAPI);
			const script = document.createElement('script');
			script.src = 'https://open.spotify.com/embed/iframe-api/v1';
			script.async = true;
			script.onerror = reject;
			document.head.appendChild(script);
		});
	}

	getPlayer(uri) {
		if(!uri && !this.#getPlayerPromise)
			return Promise.reject(new ReferenceError('No URI specified'));

		return this.#getPlayerPromise ||= new Promise((resolve, reject) => {
			SpotifyPlayer.getApi().then(API => {
				const container = document.getElementById(this.elemId);
				const elem = document.createElement('div');
				container.appendChild(elem);

				try {
					API.createController(elem, { uri, width: '100%', height: '100%' }, ctrl => {
						ctrl.addListener('ready', () => resolve(ctrl));
						ctrl.addListener('playback_started', () => emit(this, 'status', { status: 'playing' }));
						ctrl.addListener('playback_update', e => {
							if(
								e.data.position === e.data.duration ||
								(this.#endSeconds > 0 && e.data.position >= this.#endSeconds * 1000)
							) {
								ctrl.pause();
								emit(this, 'status', { status: 'finished' });
							} else {
								emit(this, 'progress', e.data.position);
							}
						});
					});
				} catch(e) {
					reject(e);
				}
			}).catch(reject);
		});
	}

	async load(url, startSeconds, timeLimit) {
		const isReload = !!this.#getPlayerPromise;
		const uri = this.getUriFromUrl(url);
		const player = await this.getPlayer(uri);
		if(isReload)
			player.loadUri(uri, true, startSeconds);
		this.#endSeconds = (startSeconds || 0) + (timeLimit || 0);
	}

	getUriFromUrl(url) {
		const u = new URL(url);
		return `spotify:${u.pathname.split('/').filter(Boolean).join(':')}`;
	}

	async play() {
		const player = await this.getPlayer();
		player.play();
	}

	async pause() {
		const player = await this.getPlayer();
		player.pause();
	}

	async togglePause() {
		const player = await this.getPlayer();
		player.togglePlay();
	}

	async stop() {
		const player = await this.getPlayer();
		player.pause();
		player.seek(0);
	}

	async cleanup() {
		if(!this.#getPlayerPromise) return;
		try {
			const player = await this.#getPlayerPromise;
			player.destroy();
		} catch(e) {
			// ignore
		} finally {
			this.#getPlayerPromise = null;
		}
	}
}
